/**
 * SchemaCreationWizard - Multi-step wizard for creating schemas
 *
 * Guides users through the schema creation process:
 * 1. File Upload - Upload sample file for inference
 * 2. Schema Review - Review and edit inferred fields
 * 3. Metadata - Provide schema name and settings
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useSchemaCreationStore,
  useWizardNavigation,
  SchemaCreationStep,
} from '@/features/schemas/store/schemaCreationStore';
import { FileUploadForInference } from './FileUploadForInference';
import { SchemaReviewStep } from './SchemaReviewStep';
import { SchemaMetadataStep } from './SchemaMetadataStep';

/**
 * Wizard step configuration
 * Note: Step IDs must match SchemaCreationStep enum values (0, 1, 2)
 */
const WIZARD_STEPS = [
  {
    id: SchemaCreationStep.UPLOAD,      // 0
    title: 'Upload File',
    description: 'Upload a sample file to infer schema',
  },
  {
    id: SchemaCreationStep.FIELD_EDITOR, // 1
    title: 'Review Schema',
    description: 'Review and modify inferred fields',
  },
  {
    id: SchemaCreationStep.METADATA,     // 2
    title: 'Schema Metadata',
    description: 'Provide schema name and settings',
  },
];

/**
 * SchemaCreationWizard Component
 *
 * Multi-step wizard for creating schemas with file upload,
 * schema inference, field editing, and metadata collection.
 *
 * @example
 * ```tsx
 * <SchemaCreationWizard />
 * ```
 */
export const SchemaCreationWizard: React.FC = () => {
  const navigate = useNavigate();
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const store = useSchemaCreationStore();
  const { currentStep, nextStep, previousStep } = useWizardNavigation();

  /**
   * Reset wizard state when component mounts
   * This ensures a clean slate when clicking "New Schema"
   */
  useEffect(() => {
    store.resetWizard();
  }, []);

  /**
   * Calculate progress percentage
   */
  const progressPercent = ((currentStep + 1) / WIZARD_STEPS.length) * 100;

  /**
   * Get current step configuration
   */
  const currentStepConfig = WIZARD_STEPS[currentStep];

  /**
   * Check if can proceed to next step
   */
  const canProceed = () => {
    switch (currentStep) {
      case SchemaCreationStep.UPLOAD:
        return store.fields.length > 0;
      case SchemaCreationStep.FIELD_EDITOR:
        return store.fields.length > 0;
      case SchemaCreationStep.METADATA:
        return false; // Metadata step handles its own submission
      default:
        return false;
    }
  };

  /**
   * Handle next button click
   */
  const handleNext = useCallback(() => {
    if (canProceed()) {
      nextStep();
    }
  }, [currentStep, nextStep, store.fields]);

  /**
   * Handle previous button click
   */
  const handlePrevious = useCallback(() => {
    previousStep();
  }, [previousStep]);

  /**
   * Handle cancel button click
   */
  const handleCancel = useCallback(() => {
    setShowCancelDialog(true);
  }, []);

  /**
   * Confirm cancellation
   */
  const handleConfirmCancel = useCallback(() => {
    store.resetWizard();
    navigate('/schemas');
  }, [store, navigate]);

  /**
   * Render step content
   */
  const renderStepContent = () => {
    switch (currentStep) {
      case SchemaCreationStep.UPLOAD:
        return <FileUploadForInference />;
      case SchemaCreationStep.FIELD_EDITOR:
        return <SchemaReviewStep />;
      case SchemaCreationStep.METADATA:
        return <SchemaMetadataStep />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Progress */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h2 className="text-2xl font-bold">
              {currentStepConfig.title}
            </h2>
            <p className="text-muted-foreground">
              {currentStepConfig.description}
            </p>
          </div>
          <Button variant="ghost" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="font-medium">
              Step {currentStep + 1} of {WIZARD_STEPS.length}
            </span>
            <span className="text-muted-foreground">
              {Math.round(progressPercent)}% complete
            </span>
          </div>
          <Progress value={progressPercent} className="w-full" />
        </div>

        {/* Step Indicators */}
        <div className="flex items-center justify-between">
          {WIZARD_STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                    transition-colors
                    ${
                      index < currentStep
                        ? 'bg-primary text-primary-foreground'
                        : index === currentStep
                        ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                        : 'bg-muted text-muted-foreground'
                    }
                  `}
                >
                  {index + 1}
                </div>
                <span
                  className={`
                    mt-2 text-xs text-center
                    ${index === currentStep ? 'font-medium' : 'text-muted-foreground'}
                  `}
                >
                  {step.title}
                </span>
              </div>
              {index < WIZARD_STEPS.length - 1 && (
                <div
                  className={`
                    flex-1 h-0.5 mx-2
                    ${index < currentStep ? 'bg-primary' : 'bg-muted'}
                  `}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="min-h-[500px]">
        {renderStepContent()}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 border-t">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentStep === 0}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        {currentStep < WIZARD_STEPS.length - 1 && (
          <Button
            onClick={handleNext}
            disabled={!canProceed()}
          >
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Schema Creation?</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel? All progress will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
            >
              Continue Editing
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmCancel}
            >
              Yes, Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
